/**
 * Shared Adesa-vs-Tekion field rendering for the Cleared List validator and
 * the candidate modal. Renders the labeled Stock:/VIN: lines with match tones
 * (green = matched, yellow = mismatched, red = missing) plus an "∗ Matched" /
 * "∗ Mismatched" marker, so the inline validator card and the modal stay
 * visually identical and the tone logic lives in exactly one place.
 */
import React from "react"

import { WholesaleTekionCandidate, WholesaleVehicle } from "../../api/wholesale"
import { THEME } from "../../config/theme"

export const norm = (s: string | null | undefined): string => (s ?? "").trim().toUpperCase()

// How a Stock/VIN field relates to the other side: matched (green),
// mismatched (yellow), or no relationship to highlight (none).
type FieldTone = "match" | "mismatch" | "none"

function MatchMarker({ tone }: { tone: FieldTone }) {
  if (tone === "match") return <span style={matchedMarkerStyle}>∗ Matched</span>
  if (tone === "mismatch") return <span style={mismatchMarkerStyle}>∗ Mismatched</span>
  return null
}

// Adesa side — ADESA tag + Stock/VIN. Tones are derived against *all* of the
// vehicle's candidates (the field is "matched" if any candidate agrees).
export function AdesaFields({ vehicle }: { vehicle: WholesaleVehicle }) {
  const candidates = vehicle.match_candidates ?? []
  const stockMissing = norm(vehicle.client) === ""
  const stockMatched =
    !stockMissing && candidates.some((c) => norm(c.tekion_stock) === norm(vehicle.client))
  const vinMissing = norm(vehicle.vin) === ""
  const vinMatched = !vinMissing && candidates.some((c) => norm(c.tekion_vin) === norm(vehicle.vin))
  const stockTone: FieldTone = stockMatched ? "match" : "mismatch"
  const vinTone: FieldTone = vinMissing ? "none" : vinMatched ? "match" : "mismatch"

  return (
    <div style={sideBlockStyle}>
      <div>
        <span style={sourceTagStyle}>Adesa</span>
      </div>
      <div>
        {stockMissing ? (
          <span style={fieldMissingStyle}>
            Stock: <span style={missingTextStyle}>Missing</span>
          </span>
        ) : (
          <span style={fieldChipStyle(stockTone)}>
            Stock: <span style={monoStyle}>{vehicle.client?.trim() || "—"}</span>
            <MatchMarker tone={stockTone} />
          </span>
        )}
      </div>
      <div>
        <span style={fieldChipStyle(vinTone)}>
          VIN: <span style={monoStyle}>{vehicle.vin ?? "—"}</span>
          <MatchMarker tone={vinTone} />
        </span>
      </div>
    </div>
  )
}

// Tekion side — TEKION tag + Stock/VIN for one candidate, tones computed
// against the Adesa row. `trailingStock` lets the modal hang its
// Current/Suggested chips off the Stock line; `withMeta` adds the
// status/title/stocked + Year Make Model lines.
export function CandidateFields({
  vehicle,
  candidate,
  withMeta = false,
  trailingStock,
}: {
  vehicle: WholesaleVehicle
  candidate: WholesaleTekionCandidate
  withMeta?: boolean
  trailingStock?: React.ReactNode
}) {
  const c = candidate
  const stockMissing = norm(vehicle.client) === ""
  const vinMissing = norm(vehicle.vin) === ""
  const stockMatched = !stockMissing && norm(c.tekion_stock) === norm(vehicle.client)
  const vinMatched = !vinMissing && norm(c.tekion_vin) === norm(vehicle.vin)
  const stockTone: FieldTone = stockMissing ? "none" : stockMatched ? "match" : "mismatch"
  const vinTone: FieldTone = vinMissing ? "none" : vinMatched ? "match" : "mismatch"

  const meta = [
    c.tekion_status,
    c.tekion_stocked_in_date ? `Stocked ${c.tekion_stocked_in_date}` : null,
    c.tekion_title_status ? `Title: ${c.tekion_title_status}` : null,
  ]
    .filter(Boolean)
    .join("  ·  ")

  return (
    <div style={sideBlockStyle}>
      <div>
        <span style={sourceTagStyle}>Tekion</span>
      </div>
      <div style={fieldRowStyle}>
        <span style={fieldChipStyle(stockTone)}>
          Stock: <span style={stockValueStyle}>{c.tekion_stock ?? "(no stock)"}</span>
          <MatchMarker tone={stockTone} />
        </span>
        {trailingStock}
      </div>
      <div>
        <span style={{ ...fieldChipStyle(vinTone), ...monoStyle }}>
          VIN: {c.tekion_vin ?? "—"}
          <MatchMarker tone={vinTone} />
        </span>
      </div>
      {withMeta && c.tekion_year_make_model && <div style={metaStyle}>{c.tekion_year_make_model}</div>}
      {withMeta && meta && <div style={metaStyle}>{meta}</div>}
    </div>
  )
}

// Compact 1:1 comparison for the inline validator: a 2-row grid (Adesa /
// Tekion) with Stock + VIN columns aligned, tones as cell highlights, and the
// candidate meta on a single trailing line. Hugs its content — no dead space.
export function CompactComparison({
  vehicle,
  candidate,
  action,
}: {
  vehicle: WholesaleVehicle
  candidate: WholesaleTekionCandidate
  // Rendered on the Tekion row, far right (e.g. the per-row approve control).
  action?: React.ReactNode
}) {
  const c = candidate
  const stockMissing = norm(vehicle.client) === ""
  const vinMissing = norm(vehicle.vin) === ""
  const stockMatch = !stockMissing && norm(c.tekion_stock) === norm(vehicle.client)
  const vinMatch = !vinMissing && norm(c.tekion_vin) === norm(vehicle.vin)
  const stockTone: FieldTone = stockMissing ? "none" : stockMatch ? "match" : "mismatch"
  const vinTone: FieldTone = vinMissing ? "none" : vinMatch ? "match" : "mismatch"

  const meta = [
    c.tekion_year_make_model,
    c.tekion_status,
    c.tekion_stocked_in_date ? `Stocked ${c.tekion_stocked_in_date}` : null,
    c.tekion_title_status ? `Title: ${c.tekion_title_status}` : null,
  ]
    .filter(Boolean)
    .join("  ·  ")

  return (
    <div style={compactWrapStyle}>
      <div style={gridStyle}>
        <span />
        <span style={colHeadStyle}>Stock</span>
        <span style={colHeadStyle}>VIN</span>
        <span />

        <span style={rowTagStyle}>Adesa</span>
        {stockMissing ? (
          <span style={missingCellStyle}>Missing</span>
        ) : (
          <span style={cellStyle(stockTone)}>{vehicle.client?.trim() || "—"}</span>
        )}
        <span style={cellStyle(vinTone)}>{vehicle.vin ?? "—"}</span>
        <span />

        <span style={rowTagStyle}>Tekion</span>
        <span style={cellStyle(stockMissing ? "none" : stockTone)}>
          {c.tekion_stock ?? "(no stock)"}
          <MatchMarker tone={stockMissing ? "none" : stockTone} />
        </span>
        <span style={cellStyle(vinTone)}>
          {c.tekion_vin ?? "—"}
          <MatchMarker tone={vinTone} />
        </span>
        <span style={actionCellStyle}>{action}</span>
      </div>
      {meta && <div style={compactMetaStyle}>{meta}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sideBlockStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  minWidth: 0,
}

// "Adesa" / "Tekion" provenance tag.
const sourceTagStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: THEME.colors.neutral.light.textSecondary,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  padding: "1px 5px",
}

const monoStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
}

const stockValueStyle: React.CSSProperties = {
  ...monoStyle,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
}

const fieldRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: THEME.spacing.sm,
  flexWrap: "wrap",
}

// The Stock/VIN value wrapper. Light highlight that hugs its content: green
// when the field tied the two sides together, yellow when present on both but
// disagreeing. inline-block (not flex) so the literal space after the label
// renders — flex items drop whitespace between them.
function fieldChipStyle(tone: FieldTone): React.CSSProperties {
  const base: React.CSSProperties = {
    display: "inline-block",
    fontSize: 12,
    color: THEME.colors.neutral.light.textSecondary,
  }
  if (tone === "none") return base
  return {
    ...base,
    background: tone === "match" ? THEME.colors.success : THEME.colors.warning,
    borderRadius: THEME.borderRadius.sm,
    padding: "1px 6px",
  }
}

const matchedMarkerStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: THEME.colors.semantic.success,
  fontFamily: THEME.fonts.primary,
  marginLeft: 6,
}

const mismatchMarkerStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: THEME.colors.semantic.warning,
  fontFamily: THEME.fonts.primary,
  marginLeft: 6,
}

// Adesa stock absent on this row — light pastel-red highlight + "Missing".
const fieldMissingStyle: React.CSSProperties = {
  display: "inline-block",
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  background: THEME.colors.danger,
  borderRadius: THEME.borderRadius.sm,
  padding: "1px 6px",
}

const missingTextStyle: React.CSSProperties = {
  fontWeight: 600,
  color: THEME.colors.semantic.danger,
  marginLeft: 4,
}

const metaStyle: React.CSSProperties = {
  fontSize: 12,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 2,
}

// --- Compact grid comparison (inline validator) ---

const compactWrapStyle: React.CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 3,
  minWidth: 0,
}

const gridStyle: React.CSSProperties = {
  display: "grid",
  // Hugs content; the action sits in its own column right after VIN, close to
  // the "∗ Matched" cue rather than shoved to the page edge.
  gridTemplateColumns: "max-content max-content max-content max-content",
  columnGap: THEME.spacing.md,
  rowGap: 3,
  alignItems: "center",
  width: "fit-content",
  maxWidth: "100%",
}

const actionCellStyle: React.CSSProperties = {
  alignSelf: "center",
}

const colHeadStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: THEME.colors.neutral.light.textSecondary,
}

const rowTagStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  color: THEME.colors.neutral.light.textSecondary,
  paddingRight: THEME.spacing.xs,
}

function cellStyle(tone: FieldTone): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    fontSize: 12,
    fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
    color: THEME.colors.neutral.light.textPrimary,
    borderRadius: THEME.borderRadius.sm,
    padding: tone === "none" ? "1px 0" : "1px 6px",
    background:
      tone === "match"
        ? THEME.colors.success
        : tone === "mismatch"
          ? THEME.colors.warning
          : "transparent",
  }
}

const missingCellStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  fontSize: 12,
  fontWeight: 600,
  color: THEME.colors.semantic.danger,
  background: THEME.colors.danger,
  borderRadius: THEME.borderRadius.sm,
  padding: "1px 6px",
}

const compactMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
}
